import clsx from 'clsx';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import ReactPaginate from 'react-paginate';
import { isNewHomeScreen2025Enabled } from '@/services/systemConfiguration.utilities';
import { Icon, Select } from '@seeqdev/qomponents';

interface PaginatorProps {
  gotoPage: (page: number) => void;
  pageCount: number;
  pageNumber: number;
  pageSize?: number;
  setPageSize?: (size: number) => void;
  insideModal?: boolean;
  total?: number;
  pageSizeOptionsCustom?: number[];
  small?: boolean;
  allowCustomPageSize?: boolean;
}

type NumericOption = {
  label: number;
  value: number;
};

type PageChangeEvent = {
  selected: number;
};

export const Paginator = ({
  gotoPage,
  pageCount,
  pageNumber,
  pageSize,
  setPageSize = _.noop,
  insideModal,
  total,
  pageSizeOptionsCustom,
  small = false,
  allowCustomPageSize = false,
}: PaginatorProps) => {
  const { t } = useTranslation();

  const hasPageSize = !_.isNil(pageSize);
  const pageSizeOptions = hasPageSize ? (pageSizeOptionsCustom ?? [5, 10, 15, 20, 50]) : [];
  const formattedOptions = _.map(
    pageSizeOptions,
    (size): NumericOption => ({
      label: size,
      value: size,
    }),
  );
  const formattedGoToPageOptions = _.map(
    _.range(1, pageCount + 1, 1),
    (size): NumericOption => ({
      label: size,
      value: size,
    }),
  );

  const handleGoToPageChange = (option: NumericOption | null) => {
    if (option) {
      gotoPage(option.value);
    }
  };

  const handlePageSizeChange = (option: NumericOption | null) => {
    if (option) {
      setPageSize(option.value);
    }
  };

  const handlePageChange = (event: PageChangeEvent) => {
    gotoPage(event.selected + 1);
  };

  return (
    <div className={`${isNewHomeScreen2025Enabled() ? '' : 'paginatorWrapper'}`}>
      <div
        className={clsx(
          'paginator',
          'flexColumnContainerWrap',
          'flexAlignCenter',
          small ? 'mr5' : 'mr10',
          insideModal ? 'flexSpaceBetween' : 'pull-right',
        )}
        data-testid="paginator">
        <div className={clsx('flexColumnContainer', small ? 'ml5' : 'ml10')}>
          <div id="goToPageWrapper" data-testid="goToPageWrapper" className="flexColumnContainer flexAlignCenter">
            {total && (
              <span className="pr10">
                {t('PAGINATOR.TOTAL')}
                <strong>{total}</strong>
              </span>
            )}
            <span className="pr5">{t('PAGINATOR.PAGE')}</span>
            <Select
              small={small}
              placeholder=""
              menuPortalTarget={document.body}
              extraClassNames={clsx(
                small ? 'width-55' : 'width-75',
                small ? 'pr5' : 'pr10',
                isNewHomeScreen2025Enabled() ? 'newHomeScreenSelect' : '',
              )}
              value={_.find(formattedGoToPageOptions, { value: pageNumber })}
              options={formattedGoToPageOptions}
              isSearchable={true}
              onChange={handleGoToPageChange}
            />
          </div>
          {hasPageSize && (
            <div id="itemCountWrapper" data-testid="itemCountWrapper" className="flexColumnContainer flexAlignCenter">
              <span className={clsx(small ? '' : 'pl10')}>{t('PAGINATOR.SHOW')}</span>
              <Select
                small={small}
                menuPlacement="auto"
                menuPortalTarget={document.body}
                extraClassNames={clsx(
                  small ? 'width-55' : 'width-75',
                  'pl5',
                  small ? '' : 'pr10',
                  isNewHomeScreen2025Enabled() ? 'newHomeScreenSelect' : '',
                )}
                value={_.find(formattedOptions, { value: pageSize })}
                options={formattedOptions}
                isSearchable={false}
                onChange={handlePageSizeChange}
                creatable={allowCustomPageSize}
              />
            </div>
          )}
        </div>
        <ReactPaginate
          pageCount={pageCount}
          pageRangeDisplayed={insideModal ? 1 : 2}
          marginPagesDisplayed={insideModal ? 1 : 2}
          previousLabel={insideModal ? <Icon icon="fa-caret-left" /> : t('PAGINATOR.PREVIOUS')}
          nextLabel={insideModal ? <Icon icon="fa-caret-right" /> : t('PAGINATOR.NEXT')}
          breakLabel={t('PAGINATOR.BREAK')}
          forcePage={pageNumber - 1}
          onPageChange={handlePageChange}
          breakClassName="break-me"
          containerClassName={clsx(
            'pagination',
            small ? 'pagination-small' : 'pagination-lg height-34',
            isNewHomeScreen2025Enabled() ? 'newHomeScreenPagination' : '',
          )}
          subContainerClassName="pages pagination"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          activeClassName="active"
          disabledClassName="disabledLook disabledBehavior"
        />
      </div>
    </div>
  );
};
